import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useQuery } from "@tanstack/react-query";
import {
  tournamentDayApi,
  type TournamentOperatorBoardParams,
} from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";
import { useEffect, useId, useMemo, useRef, type RefObject } from "react";
import { useSearchParams } from "react-router-dom";

type MatchRef = Schema["TournamentMatchRef"];
type Board = Schema["TournamentOperatorBoard"];
type BoardCell = Schema["TournamentOperatorBoardCell"];

type Props = {
  focusHeadingRef?: RefObject<HTMLHeadingElement | null>;
  onSelectMatch?: (match: MatchRef) => void;
  revision: number;
  selectedMatch?: MatchRef | null;
  tournamentId: string;
};

const boardWindows = [60, 120, 240] as const;

function textState(value: string): string {
  return value.replaceAll("_", " ");
}

function hasErrorCode(error: unknown, code: string): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: unknown }).code === code
  );
}

function formatTime(value: string, venueTimezone: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: venueTimezone,
    }).format(new Date(value));
  } catch {
    return "Time unavailable";
  }
}

function formatDay(value: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "short",
      // ``day`` is a facility-local calendar label, not an instant. Formatting
      // it in UTC preserves that exact date even for UTC+14 venues.
      timeZone: "UTC",
      weekday: "short",
      year: "numeric",
    }).format(new Date(`${value}T12:00:00Z`));
  } catch {
    return value;
  }
}

function actualStatus(cell: BoardCell, venueTimezone: string): string | null {
  if (cell.completed_at)
    return `Completed ${formatTime(cell.completed_at, venueTimezone)}`;
  if (cell.started_at)
    return `Started ${formatTime(cell.started_at, venueTimezone)}`;
  if (cell.called_at)
    return `Call time ${formatTime(cell.called_at, venueTimezone)}`;
  return null;
}

function cellState(cell: BoardCell): string {
  return [
    textState(cell.execution_state),
    textState(cell.dispatch_state),
    cell.pinned ? "pinned" : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function boardCellGeometry(
  cell: BoardCell,
  windowStartAt: string,
  windowEndAt: string,
): { left: string; width: string } {
  const windowStart = new Date(windowStartAt).getTime();
  const windowEnd = new Date(windowEndAt).getTime();
  const start = new Date(cell.planned_start_at).getTime();
  const end = new Date(cell.planned_end_at).getTime();
  const span = windowEnd - windowStart;
  if (!Number.isFinite(span) || span <= 0) return { left: "0%", width: "100%" };
  const left = Math.max(0, Math.min(100, ((start - windowStart) / span) * 100));
  const right = Math.max(
    left,
    Math.min(100, ((end - windowStart) / span) * 100),
  );
  return {
    left: `${left}%`,
    width: `${Math.max(0, Math.min(100 - left, right - left))}%`,
  };
}

export function boardLaneNeedsList(
  cells: BoardCell[],
  windowStartAt: string,
  windowEndAt: string,
): boolean {
  const intervals = cells
    .map((cell) => ({
      cell,
      end: new Date(cell.planned_end_at).getTime(),
      start: new Date(cell.planned_start_at).getTime(),
      width: Number.parseFloat(
        boardCellGeometry(cell, windowStartAt, windowEndAt).width,
      ),
    }))
    .sort((left, right) => left.start - right.start || left.end - right.end);
  return intervals.some(
    (interval, index) =>
      interval.width < 8 ||
      (index > 0 && interval.start < intervals[index - 1].end),
  );
}

function BoardMatchButton({
  cell,
  compact = false,
  onSelect,
  selected,
  venueTimezone,
}: {
  cell: BoardCell;
  compact?: boolean;
  onSelect: () => void;
  selected: boolean;
  venueTimezone: string;
}) {
  const descriptionId = useId();
  const actual = actualStatus(cell, venueTimezone);
  const plannedRange = `${formatTime(cell.planned_start_at, venueTimezone)}–${formatTime(cell.planned_end_at, venueTimezone)}`;
  const description = [
    cell.court_label,
    `Planned ${plannedRange}`,
    `Status ${cellState(cell)}`,
    actual,
  ]
    .filter(Boolean)
    .join(". ");
  return (
    <Button
      aria-describedby={descriptionId}
      aria-label={`Inspect ${cell.match_display_label}`}
      onClick={onSelect}
      size="small"
      sx={{
        alignItems: "flex-start",
        borderColor: selected ? "primary.main" : "divider",
        justifyContent: "flex-start",
        minHeight: compact ? 0 : 58,
        px: 1,
        py: 0.75,
        textAlign: "left",
        textTransform: "none",
        width: "100%",
      }}
      variant={selected ? "contained" : "outlined"}
    >
      <Stack spacing={0.25} sx={{ alignItems: "flex-start", minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontSize: "0.75rem",
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          }}
        >
          {cell.match_display_label}
        </Typography>
        <Typography component="span" sx={{ fontSize: "0.72rem" }}>
          {plannedRange}
        </Typography>
        <Typography component="span" sx={{ fontSize: "0.72rem" }}>
          {cellState(cell)}
        </Typography>
        {actual ? (
          <Typography component="span" sx={{ fontSize: "0.72rem" }}>
            {actual}
          </Typography>
        ) : null}
        <Box
          component="span"
          id={descriptionId}
          sx={{
            border: 0,
            clip: "rect(0 0 0 0)",
            height: 1,
            margin: -1,
            overflow: "hidden",
            p: 0,
            position: "absolute",
            whiteSpace: "nowrap",
            width: 1,
          }}
        >
          {description}
        </Box>
      </Stack>
    </Button>
  );
}

export function OperatorOperationsBoard({
  focusHeadingRef,
  onSelectMatch,
  revision,
  selectedMatch,
  tournamentId,
}: Props) {
  const desktop = useMediaQuery("(min-width:900px)");
  const [searchParams, setSearchParams] = useSearchParams();
  const day = searchParams.get("day") || undefined;
  const rawWindowMinutes = Number(searchParams.get("window_minutes"));
  const windowMinutes: (typeof boardWindows)[number] =
    rawWindowMinutes === 60 || rawWindowMinutes === 240
      ? rawWindowMinutes
      : 120;
  const params = useMemo<TournamentOperatorBoardParams | null>(
    () =>
      day
        ? {
            courtId: searchParams.get("court_id") || undefined,
            day,
            drawId: searchParams.get("draw_id") || undefined,
            eventId: searchParams.get("event_id") || undefined,
            query: searchParams.get("query") || undefined,
            windowMinutes,
            windowStartAt: searchParams.get("window_start_at") || undefined,
          }
        : null,
    [day, searchParams, windowMinutes],
  );
  const boardContextKey = params
    ? `${tournamentId}:${JSON.stringify(params)}`
    : null;
  const retainedBoardRef = useRef<{
    contextKey: string;
    value: Board;
  } | null>(null);
  const board = useQuery({
    enabled: Boolean(tournamentId && params),
    queryFn: () => {
      if (!params) throw new Error("A tournament day is required.");
      return tournamentDayApi.getTournamentOperatorBoard(tournamentId, params);
    },
    queryKey: [
      "tournament-operator-board",
      tournamentId,
      revision,
      boardContextKey,
    ],
    retry: false,
  });
  useEffect(() => {
    if (!board.data || !boardContextKey) return;
    retainedBoardRef.current = {
      contextKey: boardContextKey,
      value: board.data,
    };
  }, [board.data, boardContextKey]);

  const updateContext = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const updateDay = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("day", value);
    else next.delete("day");
    next.delete("window_start_at");
    setSearchParams(next, { replace: true });
  };
  const updateWindowMinutes = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("window_minutes", value);
    next.delete("window_start_at");
    setSearchParams(next, { replace: true });
  };
  const moveWindow = (direction: -1 | 1) => {
    const value = board.data?.window_start_at;
    if (!value) return;
    const nextStart =
      new Date(value).getTime() + direction * windowMinutes * 60_000;
    if (!Number.isFinite(nextStart)) return;
    updateContext("window_start_at", new Date(nextStart).toISOString());
  };
  const data =
    board.data ??
    (retainedBoardRef.current?.contextKey === boardContextKey
      ? retainedBoardRef.current.value
      : undefined);
  const boardErrorMessage = hasErrorCode(
    board.error,
    "TOURNAMENT_PLAN_INFEASIBLE",
  )
    ? "This Board window cannot be shown completely. Choose a shorter window or another tournament day."
    : "The court board is unavailable. Load the current board.";

  return (
    <Paper
      aria-label="Operations Board"
      aria-busy={Boolean(data && board.isFetching)}
      component="section"
      sx={{ p: 2 }}
      variant="outlined"
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          sx={{ alignItems: { md: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Typography
              component="h2"
              ref={focusHeadingRef}
              tabIndex={-1}
              variant="h6"
            >
              Court board
            </Typography>
            <Typography color="text.secondary" variant="body2">
              A bounded court-by-time view of the published tournament plan.
            </Typography>
          </Box>
          {data ? (
            <Typography color="text.secondary" variant="body2">
              {data.cell_count} planned{" "}
              {data.cell_count === 1 ? "match" : "matches"}
            </Typography>
          ) : null}
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField
            label="Tournament day"
            onChange={(event) => updateDay(event.target.value)}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            type="date"
            value={day ?? ""}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="operations-board-window-label">Window</InputLabel>
            <Select
              label="Window"
              labelId="operations-board-window-label"
              onChange={(event) => updateWindowMinutes(event.target.value)}
              value={String(windowMinutes)}
            >
              {boardWindows.map((minutes) => (
                <MenuItem key={minutes} value={String(minutes)}>
                  {minutes / 60} {minutes === 60 ? "hour" : "hours"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            disabled={!data || board.isFetching}
            onClick={() => moveWindow(-1)}
            size="small"
          >
            Earlier window
          </Button>
          <Button
            disabled={!data || board.isFetching}
            onClick={() => moveWindow(1)}
            size="small"
          >
            Later window
          </Button>
        </Stack>

        {!day ? (
          <Alert severity="info">
            Choose a tournament day to load its published court plan.
          </Alert>
        ) : null}
        {!data && board.isPending && day ? (
          <Typography aria-live="polite">Loading court board…</Typography>
        ) : null}
        {data && board.isFetching ? (
          <Typography aria-live="polite" color="text.secondary" variant="body2">
            Refreshing court board…
          </Typography>
        ) : null}
        {board.isError ? (
          <Alert
            action={
              <Button onClick={() => void board.refetch()} size="small">
                Load current board
              </Button>
            }
            role="alert"
            severity="error"
          >
            {boardErrorMessage}
          </Alert>
        ) : null}
        {data && !board.isError ? (
          <>
            <Typography color="text.secondary" variant="body2">
              {formatDay(day ?? "")} ·{" "}
              {formatTime(data.window_start_at, data.venue_timezone)}–
              {formatTime(data.window_end_at, data.venue_timezone)}
            </Typography>
            {desktop ? (
              <Box
                aria-label="Court-by-time lanes"
                sx={{ border: 1, borderColor: "divider", overflowX: "auto" }}
              >
                <Box sx={{ minWidth: 780 }}>
                  <Box
                    sx={{
                      backgroundColor: "background.paper",
                      borderBottom: 1,
                      borderColor: "divider",
                      display: "grid",
                      gridTemplateColumns: "190px minmax(560px, 1fr)",
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    <Typography sx={{ p: 1 }} variant="subtitle2">
                      Court
                    </Typography>
                    <Stack
                      direction="row"
                      sx={{ justifyContent: "space-between", p: 1 }}
                    >
                      <Typography variant="subtitle2">
                        {formatTime(data.window_start_at, data.venue_timezone)}
                      </Typography>
                      <Typography variant="subtitle2">
                        {formatTime(data.window_end_at, data.venue_timezone)}
                      </Typography>
                    </Stack>
                  </Box>
                  {data.lanes.map((lane) => (
                    <Box
                      key={lane.court_id}
                      sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "grid",
                        gridTemplateColumns: "190px minmax(560px, 1fr)",
                        minHeight: 80,
                      }}
                    >
                      <Typography
                        sx={{
                          alignSelf: "stretch",
                          backgroundColor: "background.paper",
                          borderRight: 1,
                          borderColor: "divider",
                          fontWeight: 700,
                          p: 1,
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                        }}
                        variant="body2"
                      >
                        {lane.court_label}
                      </Typography>
                      {boardLaneNeedsList(
                        lane.cells ?? [],
                        data.window_start_at,
                        data.window_end_at,
                      ) ? (
                        <Stack spacing={0.5} sx={{ p: 0.75 }}>
                          <Typography color="text.secondary" variant="caption">
                            Dense timing is listed by planned time.
                          </Typography>
                          {(lane.cells ?? []).map((cell) => {
                            const selected =
                              selectedMatch?.kind === cell.match.kind &&
                              selectedMatch?.match_id === cell.match.match_id;
                            return (
                              <BoardMatchButton
                                cell={cell}
                                compact
                                key={`${cell.match.kind}:${cell.match.match_id}`}
                                onSelect={() => onSelectMatch?.(cell.match)}
                                selected={selected}
                                venueTimezone={data.venue_timezone}
                              />
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box sx={{ minHeight: 78, position: "relative" }}>
                          {(lane.cells ?? []).map((cell) => {
                            const placement = boardCellGeometry(
                              cell,
                              data.window_start_at,
                              data.window_end_at,
                            );
                            const selected =
                              selectedMatch?.kind === cell.match.kind &&
                              selectedMatch?.match_id === cell.match.match_id;
                            return (
                              <Box
                                key={`${cell.match.kind}:${cell.match.match_id}`}
                                sx={{
                                  left: placement.left,
                                  p: 0.5,
                                  position: "absolute",
                                  top: 0,
                                  width: placement.width,
                                }}
                              >
                                <BoardMatchButton
                                  cell={cell}
                                  onSelect={() => onSelectMatch?.(cell.match)}
                                  selected={selected}
                                  venueTimezone={data.venue_timezone}
                                />
                              </Box>
                            );
                          })}
                          {(lane.cells ?? []).length === 0 ? (
                            <Typography
                              color="text.secondary"
                              sx={{ p: 1 }}
                              variant="body2"
                            >
                              No planned match in this window.
                            </Typography>
                          ) : null}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Stack spacing={1}>
                {data.lanes.map((lane) => (
                  <Paper
                    component="section"
                    key={lane.court_id}
                    sx={{ p: 1 }}
                    variant="outlined"
                  >
                    <Stack spacing={0.75}>
                      <Typography sx={{ fontWeight: 700 }} variant="body2">
                        {lane.court_label}
                      </Typography>
                      {(lane.cells ?? []).map((cell) => {
                        const selected =
                          selectedMatch?.kind === cell.match.kind &&
                          selectedMatch?.match_id === cell.match.match_id;
                        return (
                          <BoardMatchButton
                            cell={cell}
                            compact
                            key={`${cell.match.kind}:${cell.match.match_id}`}
                            onSelect={() => onSelectMatch?.(cell.match)}
                            selected={selected}
                            venueTimezone={data.venue_timezone}
                          />
                        );
                      })}
                      {(lane.cells ?? []).length === 0 ? (
                        <Typography color="text.secondary" variant="body2">
                          No planned match in this window.
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
            {data.lanes.length === 0 ? (
              <Typography color="text.secondary">
                No allocated courts match this Board context.
              </Typography>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}
